import React, { useEffect, useMemo, useState } from 'react';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import api from '../../services/api';
import { MetricForm } from './MetricPicker';
import DistributionStrip from './DistributionStrip';
import { baseLineOptions, seriesColour, shortDate } from './charts';
import { NAVY, TEAL, LIGHT_BLUE, MAUVE, TEAL_DARK } from './palette';
import { errorText, fmtValue, metricHeading } from './format';

const VIEWS = [
  ['distribution', 'Distribution'], ['rolling', 'Rolling'], ['correlation', 'Correlation'], ['scatter', 'Scatter'], ['groups', 'Style / Tilt groups'],
];

const corrStyle = (v) => {
  if (v === null || v === undefined) return undefined;
  const a = Math.min(1, Math.abs(v)) * 0.75;
  return { background: v >= 0 ? `rgba(51, 123, 145, ${a.toFixed(2)})` : `rgba(170, 128, 144, ${a.toFixed(2)})`, color: Math.abs(v) > 0.6 ? '#fff' : undefined };
};

// Analysis views over the API: distribution, rolling series, correlation, scatter and the
// Style / Tilt group comparison. Funds default to the rows selected on the Screen tab.
export default function AnalysisTab({ catalogue, universe, selectedCodes, selectedNames, defaultSac, asAt }) {
  const [view, setView] = useState('distribution');
  const [metric, setMetric] = useState({ statistic: 'excess_return', period: '3y', rolling: null, summary: 'latest' });
  const [metricY, setMetricY] = useState({ statistic: 'information_ratio', period: '3y', rolling: null, summary: 'latest' });
  const [sac, setSac] = useState(defaultSac || '');
  const [fundsText, setFundsText] = useState((selectedCodes || []).join(', '));
  const [basis, setBasis] = useState('total');
  const [groupBy, setGroupBy] = useState('Style');
  const [excludePassive, setExcludePassive] = useState(true);
  const [data, setData] = useState(null);
  const [rollingGroups, setRollingGroups] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (defaultSac && !sac) setSac(defaultSac); }, [defaultSac, sac]);
  useEffect(() => { if ((selectedCodes || []).length) setFundsText(selectedCodes.join(', ')); }, [selectedCodes]);
  const funds = useMemo(() => fundsText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean), [fundsText]);
  const sacOptions = universe?.sub_asset_classes || [];
  const metricForApi = (m) => ({ statistic: m.statistic, period: m.period, start: m.period === 'custom' ? m.start : undefined, rolling: m.rolling || null, summary: m.summary || 'latest' });

  const run = async () => {
    setBusy(true); setErr(null); setData(null); setRollingGroups(null);
    try {
      let r;
      if (view === 'distribution') {
        r = await api.post('/api/quant-screener/analysis/distribution', { metric: metricForApi(metric), sub_asset_class: sac, as_at: asAt || null, highlight: funds });
      } else if (view === 'rolling') {
        const m = metricForApi(metric.rolling ? metric : { ...metric, rolling: 12 });
        r = await api.post('/api/quant-screener/analysis/rolling', { metric: m, funds, as_at: asAt || null, sub_asset_class: sac || null });
      } else if (view === 'correlation') {
        r = await api.post('/api/quant-screener/analysis/correlation', { funds, basis, period: metric.period, start: metric.period === 'custom' ? metric.start : undefined, as_at: asAt || null });
      } else if (view === 'scatter') {
        r = await api.post('/api/quant-screener/analysis/scatter', { metric_x: metricForApi(metric), metric_y: metricForApi(metricY), sub_asset_class: sac, as_at: asAt || null, highlight: funds });
      } else {
        r = await api.post('/api/quant-screener/analysis/groups', { metric: metricForApi(metric), sub_asset_class: sac, group_by: groupBy, as_at: asAt || null, exclude_passive: excludePassive });
        const rm = metricForApi(metric.rolling ? metric : { ...metric, rolling: 12 });
        try {
          const rr = await api.post('/api/quant-screener/analysis/groups-rolling', { metric: rm, sub_asset_class: sac, group_by: groupBy, as_at: asAt || null, exclude_passive: excludePassive });
          setRollingGroups(rr.data);
        } catch (e) { setRollingGroups({ error: errorText(e) }); }
      }
      setData(r.data);
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };

  const needsSac = view === 'distribution' || view === 'scatter' || view === 'groups';
  const needsFunds = view === 'rolling' || view === 'correlation';

  return (
    <div className="qs-analysis">
      <div className="qs-tabs qs-subtabs">
        {VIEWS.map(([k, label]) => (
          <button key={k} type="button" className={`qs-tab${view === k ? ' active' : ''}`} onClick={() => { setView(k); setData(null); }}>{label}</button>
        ))}
      </div>
      <div className="qs-block">
        <div className="qs-block-title">{view === 'scatter' ? 'X statistic' : view === 'correlation' ? 'Window' : 'Statistic'}</div>
        {view !== 'correlation' && (
          <MetricForm catalogue={catalogue} asAtDates={universe?.as_at_dates} onAdd={setMetric} buttonLabel="Use" initial={metric} />
        )}
        <div className="qs-muted">Current: <b>{metricHeading(metric, catalogue)}</b>{view === 'rolling' || view === 'groups' ? (metric.rolling ? '' : ' (rolling 12m is used for the time series)') : ''}</div>
        {view === 'scatter' && (
          <>
            <div className="qs-block-title" style={{ marginTop: 8 }}>Y statistic</div>
            <MetricForm catalogue={catalogue} asAtDates={universe?.as_at_dates} onAdd={setMetricY} buttonLabel="Use" initial={metricY} />
            <div className="qs-muted">Current: <b>{metricHeading(metricY, catalogue)}</b></div>
          </>
        )}
        <div className="qs-metric-form" style={{ marginTop: 8 }}>
          {needsSac && (
            <label className="qs-field">Sub asset class
              <select value={sac} onChange={(e) => setSac(e.target.value)}>
                <option value="">choose…</option>
                {sacOptions.map((s) => <option key={s.value} value={s.value}>{s.value} ({s.count})</option>)}
              </select>
            </label>
          )}
          {view === 'rolling' && (
            <label className="qs-field">Cohort band
              <select value={sac} onChange={(e) => setSac(e.target.value)}>
                <option value="">fund&apos;s own</option>
                {sacOptions.map((s) => <option key={s.value} value={s.value}>{s.value}</option>)}
              </select>
            </label>
          )}
          {(needsFunds || view === 'distribution' || view === 'scatter') && (
            <label className="qs-field qs-funds-field">{needsFunds ? 'Funds (TR codes)' : 'Highlight (TR codes)'}
              <input type="text" value={fundsText} onChange={(e) => setFundsText(e.target.value)} placeholder="select rows on the Screen tab or type codes" />
            </label>
          )}
          {view === 'correlation' && (
            <>
              <label className="qs-field">Basis
                <select value={basis} onChange={(e) => setBasis(e.target.value)}>
                  <option value="total">total returns</option>
                  <option value="active">active returns (vs benchmark)</option>
                </select>
              </label>
              <label className="qs-field">Period
                <select value={metric.period} onChange={(e) => setMetric({ ...metric, period: e.target.value })}>
                  {(catalogue?.periods || []).map((p) => <option key={p} value={p}>{p === 'si' ? 'since inception' : p === 'custom' ? 'custom (from…)' : p}</option>)}
                </select>
              </label>
              {metric.period === 'custom' && (
                <label className="qs-field">From
                  <select value={metric.start || ''} onChange={(e) => setMetric({ ...metric, start: e.target.value })}>
                    <option value="">from…</option>
                    {(universe?.as_at_dates || []).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
              )}
            </>
          )}
          {view === 'groups' && (
            <>
              <label className="qs-field">Group by
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                  <option value="Style">Style</option>
                  <option value="Tilt">Tilt</option>
                </select>
              </label>
              <label className="qs-field"><input type="checkbox" checked={excludePassive} onChange={(e) => setExcludePassive(e.target.checked)} /> exclude passive styles</label>
            </>
          )}
          <button type="button" className="qs-btn qs-btn-primary" onClick={run} disabled={busy || (needsSac && !sac) || (needsFunds && funds.length < (view === 'correlation' ? 2 : 1))}>
            {busy ? 'Running…' : 'Run'}
          </button>
        </div>
      </div>
      {err && <div className="qs-alert">{err}</div>}

      {data && view === 'distribution' && (
        <>
          <DistributionStrip title={`Distribution of ${metricHeading(data.metric, catalogue)}`} unit={data.unit}
            note={`${data.sub_asset_class}: ${data.n_valued} of ${data.n_cohort} funds as at ${data.as_at}`}
            points={data.points.map((p) => ({ tr: p.tr_code, name: p.name, v: p.value, held: p.held }))}
            quantiles={data.quantiles} benchmark={data.benchmark?.value} selected={new Set(funds)} onScreen={new Set(data.points.filter((p) => p.held).map((p) => p.tr_code))} />
          <div className="qs-tile">
            <div className="qs-tile-title">Ranked <span className="qs-muted">teal = held by Drummond, navy = highlighted</span></div>
            <div className="qs-grid-scroll" style={{ maxHeight: 360 }}>
              <table className="qs-grid">
                <thead><tr><th className="qs-l">Fund</th><th>Value</th><th>Percentile</th></tr></thead>
                <tbody>
                  {[...data.points].reverse().map((p) => (
                    <tr key={p.tr_code} className={funds.includes(p.tr_code) ? 'qs-row-sel' : ''}>
                      <td className="qs-l">{p.name || p.tr_code}{p.held && <span className="qs-tag qs-tag-held">held</span>}</td>
                      <td className="qs-num">{fmtValue(p.value, data.unit)}</td>
                      <td className="qs-num">{p.percentile === null ? '–' : Math.round(p.percentile)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.excluded?.length > 0 && <div className="qs-muted">Excluded (no value): {data.excluded.map((e) => `${e.tr_code} (${e.reason})`).join(', ')}</div>}
          </div>
        </>
      )}

      {data && view === 'rolling' && (
        <div className="qs-tile qs-tile-wide">
          <div className="qs-tile-title">{metricHeading(data.metric, catalogue)} <span className="qs-muted">rolling {data.metric.rolling}m; band = cohort median and inter-quartile range{data.cohort ? ` (${data.cohort.sub_asset_class})` : ''}</span></div>
          <div style={{ height: 360 }}>
            <Line
              data={{
                labels: data.dates.map(shortDate),
                datasets: [
                  ...(data.cohort ? [
                    { label: 'cohort q3', data: data.cohort.q3, borderColor: 'rgba(0,0,0,0)', backgroundColor: 'rgba(187, 203, 215, 0.35)', fill: '+1', pointRadius: 0 },
                    { label: 'cohort q1', data: data.cohort.q1, borderColor: 'rgba(0,0,0,0)', backgroundColor: 'rgba(187, 203, 215, 0.35)', fill: false, pointRadius: 0 },
                    { label: 'cohort median', data: data.cohort.median, borderColor: '#5E819C', borderDash: [4, 3], backgroundColor: '#5E819C' },
                  ] : []),
                  ...Object.keys(data.series).map((f, i) => ({ label: data.names[f] || f, data: data.series[f], borderColor: seriesColour(i), backgroundColor: seriesColour(i) })),
                ],
              }}
              options={baseLineOptions(data.unit)}
            />
          </div>
        </div>
      )}

      {data && view === 'correlation' && (
        <div className="qs-tile qs-tile-wide">
          <div className="qs-tile-title">Correlation of {data.basis} returns <span className="qs-muted">{data.period === 'si' ? 'since inception' : data.period} to {data.as_at}; pairs need {data.min_overlap} overlapping months</span></div>
          <div className="qs-heat-scroll">
            <table className="qs-heat qs-corr">
              <thead><tr><th className="qs-l" /> {data.funds.map((f) => <th key={f} title={data.names[f] || f}>{f}</th>)}</tr></thead>
              <tbody>
                {data.funds.map((a, i) => (
                  <tr key={a}>
                    <td className="qs-l" title={a}>{(data.names[a] || a).slice(0, 40)}</td>
                    {data.funds.map((b, j) => (
                      <td key={b} style={corrStyle(data.matrix[i][j])} title={`${data.overlap_months[i][j]} months overlap`}>
                        {data.matrix[i][j] === null ? '–' : data.matrix[i][j].toFixed(2)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && view === 'scatter' && (
        <div className="qs-tile qs-tile-wide">
          <div className="qs-tile-title">{metricHeading(data.x, catalogue)} vs {metricHeading(data.y, catalogue)} <span className="qs-muted">{data.sub_asset_class}: {data.n_plotted} of {data.n_cohort} funds; teal = held, navy = highlighted</span></div>
          <div style={{ height: 380 }}>
            <Scatter
              data={{ datasets: [
                { label: 'cohort', data: data.points.filter((p) => !p.held && !p.highlight).map((p) => ({ x: p.x, y: p.y, name: p.name })), backgroundColor: LIGHT_BLUE, pointRadius: 3 },
                { label: 'held', data: data.points.filter((p) => p.held && !p.highlight).map((p) => ({ x: p.x, y: p.y, name: p.name })), backgroundColor: TEAL, pointRadius: 4 },
                { label: 'highlighted', data: data.points.filter((p) => p.highlight).map((p) => ({ x: p.x, y: p.y, name: p.name })), backgroundColor: NAVY, pointRadius: 6 },
              ] }}
              options={{
                responsive: true, maintainAspectRatio: false, animation: false,
                scales: {
                  x: { title: { display: true, text: metricHeading(data.x, catalogue), font: { size: 10 } }, ticks: { font: { size: 10 }, callback: (v) => fmtValue(v, data.x.unit) }, grid: { color: '#EEF1F4' } },
                  y: { title: { display: true, text: metricHeading(data.y, catalogue), font: { size: 10 } }, ticks: { font: { size: 10 }, callback: (v) => fmtValue(v, data.y.unit) }, grid: { color: '#EEF1F4' } },
                },
                plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 }, usePointStyle: true } },
                  tooltip: { callbacks: { label: (c) => `${c.raw.name}: ${fmtValue(c.raw.x, data.x.unit)}, ${fmtValue(c.raw.y, data.y.unit)}` } } },
              }}
            />
          </div>
        </div>
      )}

      {data && view === 'groups' && (
        <>
          <div className="qs-tile qs-tile-wide">
            <div className="qs-tile-title">{metricHeading(data.metric, catalogue)} by {data.group_by} in {data.sub_asset_class} <span className="qs-muted">as at {data.as_at}{data.exclude_passive ? '; passive styles excluded' : ''}; group series needs ≥ {data.min_group_funds_per_month} funds each month</span></div>
            <div className="qs-grid-scroll" style={{ maxHeight: 320 }}>
              <table className="qs-grid">
                <thead><tr><th className="qs-l">{data.group_by}</th><th>Funds</th><th>Valued</th><th>Average of funds</th><th>Median of funds</th><th>Avg percentile</th><th>Group series</th></tr></thead>
                <tbody>
                  {data.groups.map((g) => (
                    <tr key={g.group} title={g.members.join(', ')}>
                      <td className="qs-l qs-sac">{g.group}</td>
                      <td className="qs-num">{g.n_funds}</td>
                      <td className="qs-num">{g.n_valued}</td>
                      <td className="qs-num">{fmtValue(g.avg_of_funds, data.unit)}</td>
                      <td className="qs-num">{fmtValue(g.median_of_funds, data.unit)}</td>
                      <td className="qs-num">{g.avg_percentile === null ? '–' : g.avg_percentile.toFixed(0)}</td>
                      <td className="qs-num" title={g.group_series_reason || ''}>{g.group_series === null ? <span className="qs-null">{g.group_series_reason || '–'}</span> : fmtValue(g.group_series, data.unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="qs-charts">
            <div className="qs-tile">
              <div className="qs-tile-title">Average of funds vs group series</div>
              <div style={{ height: Math.max(180, 26 * data.groups.length + 60) }}>
                <Bar
                  data={{ labels: data.groups.map((g) => `${g.group} (${g.n_funds})`), datasets: [
                    { label: 'average of funds', data: data.groups.map((g) => g.avg_of_funds), backgroundColor: TEAL },
                    { label: 'group series', data: data.groups.map((g) => g.group_series), backgroundColor: MAUVE },
                  ] }}
                  options={{ indexAxis: 'y', responsive: true, maintainAspectRatio: false, animation: false,
                    scales: { x: { ticks: { font: { size: 10 }, callback: (v) => fmtValue(v, data.unit) }, grid: { color: '#EEF1F4' } }, y: { ticks: { font: { size: 10 } }, grid: { display: false } } },
                    plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtValue(c.parsed.x, data.unit)}` } } } }}
                />
              </div>
            </div>
            {rollingGroups && !rollingGroups.error && (
              <div className="qs-tile">
                <div className="qs-tile-title">Group series over time <span className="qs-muted">rolling {rollingGroups.metric.rolling}m {rollingGroups.metric.statistic}</span></div>
                <div style={{ height: 300 }}>
                  <Line
                    data={{ labels: rollingGroups.dates.map(shortDate), datasets: Object.keys(rollingGroups.series).map((g, i) => ({
                      label: `${g} (${rollingGroups.n_funds[g]})`, data: rollingGroups.series[g], borderColor: seriesColour(i), backgroundColor: seriesColour(i),
                    })) }}
                    options={baseLineOptions(rollingGroups.unit)}
                  />
                </div>
              </div>
            )}
            {rollingGroups?.error && <div className="qs-tile"><div className="qs-muted">Rolling group series: {rollingGroups.error}</div></div>}
          </div>
        </>
      )}
      {!data && !busy && !err && (
        <div className="qs-muted qs-editor-empty">
          {selectedNames?.length ? `Selected on the Screen tab: ${selectedNames.join(', ')}.` : 'Select rows on the Screen tab to pre-fill the funds, or type TR codes.'} Then choose a statistic and run.
          <span style={{ color: TEAL_DARK }}> </span>
        </div>
      )}
    </div>
  );
}
